import React from 'react';
import {
  Box,
  Typography,
  Grid,
  useMediaQuery,
  type Theme,
} from '@mui/material';
import sanitizeHtml from 'sanitize-html';
import { Flex, FlexList, useI18nContext } from '@procore/core-react';
import { useStyles } from './PublicProjectHeader.styles';
import { type Project } from '../../types/project';
import DescriptionWithIconAndTitle from './../Business/DescriptionWithIconAndTitle';
import ExpandableText from '../Layout/ExpandableText';
import EmailObfuscationWrapper from '../utils/EmailObfuscationWrapper';
import AddressIcon from '../Icons/AddressIcon';
import CalendarDueIcon from '../Icons/CalendarDueIcon';
import DocumentPencilIcon from '../Icons/DocumentPencilIcon';
import FullCalendarIcon from '../Icons/FullCalendarIcon';
import LockedShieldIcon from '../Icons/LockedShieldIcon';
import { formatDate, formatDatetime } from '../../helpers/format-datetime';
import { getBidDueStatus } from '../../helpers/build-texts';
import {
  getNdaProjectDisplay,
  resolveProjectTitle,
} from '../../helpers/nda-display';

interface Props {
  project: Project;
}

const PublicProjectHeader = ({ project }: Props) => {
  const I18n = useI18nContext();
  const { status } = getBidDueStatus(project);
  const classes = useStyles();
  const inMobileMode = useMediaQuery(
    (theme: Theme) => theme.breakpoints.down(600),
    { noSsr: true }
  );
  const ndaDisplay = getNdaProjectDisplay(project);
  const projectDisplayName = resolveProjectTitle(
    project,
    ndaDisplay,
    I18n.t(
      'pages.j.public_project_layout.public_project_header.obfuscated_project_name'
    )
  );

  return (
    <Box className={classes.projectHeaderContainer}>
      <Flex className={classes.projectHeader}>
        <Box className={classes.projectNameContainer}>
          <FlexList
            className={classes.mainInfoContainer}
            boxProps={{ marginLeft: 'none' }}
            alignItems="baseline"
            justifyContent="space-between"
            wrap="wrap"
            data-test-id="project-header-title-container"
          >
            <Typography variant="h1" className={classes.projectName}>
              <EmailObfuscationWrapper>
                {projectDisplayName}
              </EmailObfuscationWrapper>
            </Typography>
          </FlexList>
          {!ndaDisplay.isNda && (
            <Grid
              container
              className={classes.descriptiveInfoContainer}
              data-test-id="business-header-description-container"
            >
              {project.location.city && project.location.stateCode && (
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <DescriptionWithIconAndTitle
                    icon={<AddressIcon />}
                    title={I18n.t(
                      'pages.j.public_project_layout.public_project_header.address_title'
                    )}
                    textComponent={
                      <Typography
                        className={classes.descriptionWithIconAndTitleText}
                        style={{ paddingRight: '15px' }}
                      >
                        {`${project.location.city}, ${project.location.stateCode}`}
                      </Typography>
                    }
                  />
                </Grid>
              )}
              {(project.bidDueDate || status === 'closed') && (
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <DescriptionWithIconAndTitle
                    icon={<CalendarDueIcon />}
                    title={I18n.t(
                      'pages.j.public_project_layout.public_project_header.bids_due_title'
                    )}
                    textComponent={
                      <Typography
                        className={classes.descriptionWithIconAndTitleText}
                        style={{ paddingRight: '15px' }}
                        suppressHydrationWarning={true}
                      >
                        {status === 'closed'
                          ? I18n.t(
                              'components.projects.project_card.bidding_closed'
                            )
                          : formatDatetime(project.bidDueDate)}
                      </Typography>
                    }
                  />
                </Grid>
              )}
              {project.prebidMeeting.date && (
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <DescriptionWithIconAndTitle
                    icon={<DocumentPencilIcon />}
                    title={I18n.t(
                      'pages.j.public_project_layout.public_project_header.pre_bid_meeting_title'
                    )}
                    textComponent={
                      <Typography
                        className={classes.descriptionWithIconAndTitleText}
                        style={{ paddingRight: '15px' }}
                        suppressHydrationWarning={true}
                      >
                        {formatDatetime(project.prebidMeeting.date)}
                      </Typography>
                    }
                  />
                </Grid>
              )}
              {project.estimatedStartDate && (
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <DescriptionWithIconAndTitle
                    icon={<FullCalendarIcon />}
                    title={I18n.t(
                      'pages.j.public_project_layout.public_project_header.start_date_title'
                    )}
                    textComponent={
                      <Typography
                        className={classes.descriptionWithIconAndTitleText}
                        style={{ paddingRight: '15px' }}
                      >
                        {formatDate(project.estimatedStartDate)}
                      </Typography>
                    }
                  />
                </Grid>
              )}
            </Grid>
          )}
          {ndaDisplay.isNda && (
            <div className={classes.ndaContainer} data-test-id="nda-notice">
              <Typography variant="h2" className={classes.ndaTitle}>
                <LockedShieldIcon className={classes.lockedShieldIcon} />
                {I18n.t(
                  'pages.j.public_project_layout.public_project_header.nda_title'
                )}
              </Typography>
              <div
                className={classes.ndaDescription}
                dangerouslySetInnerHTML={{
                  __html: I18n.t(
                    'pages.j.public_project_layout.public_project_header.nda_description',
                    {
                      companyName: sanitizeHtml(project.companyName),
                    }
                  ),
                }}
              ></div>
            </div>
          )}
        </Box>
      </Flex>
      {!ndaDisplay.isNda && (
        <Box className={classes.aboutContainer}>
          <Typography variant="h2" className={classes.aboutTitle}>
            {I18n.t(
              'pages.j.public_project_layout.public_project_header.project_description'
            )}
          </Typography>
          <ExpandableText
            text={project.projectDescription}
            textClassName={classes.about}
            expandTextClassName={classes.showMoreOrLessText}
            textCutoffLength={inMobileMode ? 220 : 800}
          />
        </Box>
      )}
    </Box>
  );
};

export default PublicProjectHeader;
